import React from 'react';

import { reportSurfaces } from '~/configs/unifiedAnalyticsTheme';

import { Box, Button, Paper, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { type Row, format, text } from './inventoryValues';
import { chosenMetric } from './model';
import { inventoryBand } from './prototypeStyle';
import { calendarColor } from './semantics';

const WEEKDAYS = ['M', 'Tu', 'W', 'Th', 'F', 'Sa', 'Su'];

/** Tableau rows are year/quarter/week; columns are month-in-quarter/weekday. */
export default function InventoryCalendar({
  rows,
  metric,
  onSelect,
  selected,
}: {
  rows: Array<Row>;
  metric: string;
  onSelect: (date: string) => void;
  selected: Array<string>;
}) {
  const theme = useTheme();
  const { rule } = reportSurfaces(theme);
  const months = [...new Set(rows.map((r) => text(r.availability_day_of_week).slice(0, 7)))].sort();
  const years = [...new Set(months.map((month) => month.slice(0, 4)))];
  const maximum = rows.reduce((value, row) => Math.max(value, chosenMetric(row, metric) ?? 0), 0);
  const ordinary = [theme.palette.error.light, theme.palette.common.white, theme.palette.success.light];
  const fixed = metric === 'booked_rate_calendar' || metric === 'missed_rm_score';
  const limit = fixed ? 100 : maximum;
  const dates = new Map(rows.map((r) => [text(r.availability_day_of_week).slice(0, 10), r]));
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
      <Box sx={{ ...inventoryBand, display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Box>
          <Typography component="h2" variant="h6" sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.2 }}>
            Rooms
          </Typography>
          <Typography variant="caption">
            Select specific month(s) for better visibility of numbers on calendar.
          </Typography>
        </Box>
        <Box aria-label="Calendar metric color scale" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption">0</Typography>
          <Box
            sx={{
              width: 180,
              height: 12,
              background: `linear-gradient(to right, ${Array.from({ length: 23 }, (_, i) => i / 22)
                .map((fraction) =>
                  calendarColor(
                    metric === 'booked_rate_calendar' ? fraction : limit * fraction,
                    metric,
                    maximum,
                    ordinary,
                  ),
                )
                .join(',')})`,
            }}
          />
          <Typography variant="caption">
            {format(metric === 'booked_rate_calendar' ? 1 : limit, metric === 'booked_rate_calendar')}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ minWidth: 850 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '36px repeat(3,minmax(0,1fr))', gap: 0.5 }}>
          <Box />
          {[0, 1, 2].map((column) => (
            <Box key={column} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))' }}>
              {WEEKDAYS.map((day) => (
                <Typography key={day} align="center" sx={{ fontSize: 9 }}>
                  {day}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>
        {years.map((year) => (
          <Box
            key={year}
            sx={{ display: 'grid', gridTemplateColumns: '36px minmax(0,1fr)', borderTop: `1px solid ${rule}` }}
          >
            <Typography sx={{ fontSize: 10, pt: 1 }}>{year}</Typography>
            <Box>
              {[0, 1, 2, 3]
                .filter((quarter) =>
                  months.some(
                    (month) => month.startsWith(year) && Math.floor((Number(month.slice(5)) - 1) / 3) === quarter,
                  ),
                )
                .map((quarter) => (
                  <Box
                    key={quarter}
                    aria-label={`${year} quarter ${quarter + 1}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
                      gap: 0.5,
                      borderBottom: `1px solid ${rule}`,
                      py: 0.25,
                    }}
                  >
                    {[0, 1, 2].map((column) => {
                      const month = `${year}-${String(quarter * 3 + column + 1).padStart(2, '0')}`;
                      const first = new Date(`${month}-01T00:00:00Z`);
                      const offset = (first.getUTCDay() + 6) % 7;
                      const days = new Date(Date.UTC(Number(year), quarter * 3 + column + 1, 0)).getUTCDate();
                      const present = months.includes(month);
                      return (
                        <Box key={month} sx={{ minWidth: 0, borderRight: `1px solid ${rule}` }}>
                          <Typography align="center" sx={{ fontSize: 10, lineHeight: '14px', fontWeight: 500 }}>
                            {present ? first.toLocaleDateString('en-AU', { month: 'long', timeZone: 'UTC' }) : '\u00a0'}
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(7,minmax(0,1fr))',
                              gridTemplateRows: 'repeat(6,12px)',
                            }}
                          >
                            {Array.from({ length: 42 }, (_, slot) => {
                              const day = slot - offset + 1;
                              const date = `${month}-${String(day).padStart(2, '0')}`;
                              const row = present && day > 0 && day <= days ? dates.get(date) : undefined;
                              if (!row) return <Box key={slot} />;
                              const value = chosenMetric(row, metric);
                              const color = calendarColor(value, metric, maximum, ordinary);
                              return (
                                <Tooltip
                                  key={slot}
                                  title={`${date} · ${format(value, metric === 'booked_rate_calendar')} · Blackout ${text(row.blackout)}`}
                                >
                                  <Button
                                    onClick={() => onSelect(date)}
                                    aria-label={`${date}: ${value === null ? 'Unknown' : format(value, metric === 'booked_rate_calendar')}`}
                                    aria-pressed={selected.includes(date)}
                                    sx={{
                                      minWidth: 0,
                                      minHeight: 0,
                                      p: 0,
                                      border: '0.5px solid #f5f5f5',
                                      borderRadius: 0,
                                      lineHeight: '11px',
                                      fontSize: 7,
                                      fontVariantNumeric: 'tabular-nums',
                                      color: '#333',
                                      bgcolor: color,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      outline: selected.includes(date) ? '2px solid #333' : undefined,
                                      outlineOffset: '-2px',
                                      '&:hover': { bgcolor: color, outline: '1px solid #333' },
                                    }}
                                  >
                                    {day} | {format(value, metric === 'booked_rate_calendar')}
                                  </Button>
                                </Tooltip>
                              );
                            })}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}
